import { useEffect } from 'react';
import { paymentPageLanguage } from '@/lib/payment-page-language';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';
import { LanguageSelector } from '@/components/LanguageSelector';
import { Button } from '@/components/ui/button';

export default function PaymentPending() {
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation('payments');

  // Payment result pages are customer-facing — respect lang param from edge function
  const langParam = paymentPageLanguage(searchParams.get('lang'));
  useEffect(() => {
    if (langParam && langParam !== i18n.language) {
      i18n.changeLanguage(langParam);
    }
  }, [langParam, i18n]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-amber-100 dark:from-yellow-950 dark:to-gray-900 flex flex-col items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 max-w-md w-full text-center">
        {/* A still clock on a "processing" screen implies something is
            happening here -- nothing is; the actual update arrives via
            WhatsApp. Slower than the default spin so it still reads as a
            clock, not a loading spinner (P-18). */}
        <Clock
          className="w-20 h-20 text-yellow-500 mx-auto mb-6 animate-spin [animation-duration:3s]"
        />
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          {t('resultPending.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {t('resultPending.description')}
        </p>

        <div className="bg-yellow-50 dark:bg-yellow-900/30 rounded-lg p-4 mb-6">
          <p className="text-sm text-yellow-700 dark:text-yellow-300">
            {t('resultPending.processingMessage')}
          </p>
          <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-2">
            {t('resultPending.noNeedToWait')}
          </p>
          {/* P-18: the customer's real exit is the WhatsApp conversation they came
              from, where the store's webhook sends the outcome. Saying it needs
              no URL parameter, unlike a wa.me button nobody passes a number to. */}
          <p className="text-sm text-yellow-800 dark:text-yellow-200 mt-2 font-medium">
            {t('resultPending.checkWhatsapp')}
          </p>
        </div>

        <Button variant="outline" onClick={() => window.close()}>
          {t('resultPending.close')}
        </Button>
      </div>

      <a
        href="https://okostec.ai"
        target="_blank"
        rel="noopener noreferrer"
        className="mt-6 flex items-center gap-2 text-gray-400 dark:text-gray-500 text-xs hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
      >
        <span>{t('poweredBy')}</span>
        <img
          src="https://storage.googleapis.com/gpt-engineer-file-uploads/YOgwp6cg3QOXTUHfe0Qjv3JHvT03/uploads/1757601334821-O.png"
          alt="Okostec"
          className="h-4 w-4"
        />
        <span className="font-medium text-gray-500 dark:text-gray-400">Okostec</span>
      </a>
    </div>
  );
}
